'use client';

import React from 'react';
import { Bell, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import LiveNewsBadge from '@/components/news/LiveNewsBadge';
import { DilutionBadge } from '@/components/stock/DilutionBadge';
import { useDilutingSymbols } from '@/hooks/useDilutingSymbols';
import { useLiveNewsSymbols } from '@/hooks/useLiveNewsSymbols';
import { useEarningsSoonSymbols } from '@/hooks/useEarningsSoonSymbols';
import { useAlertSymbols } from '@/hooks/useAlertSymbols';
import { useDividendSoonSymbols } from '@/hooks/useDividendSoonSymbols';
import { earningsBadgeTier, formatEarningsBadgeTooltip, EARNINGS_TIER_CLASSES } from '@/lib/earningsBadge';
import { dividendBadgePhase } from '@/lib/dividendBadge';
import { DividendGlyph } from '@/components/shared/DividendBadge';

/**
 * Reusable status-badge cluster for a ticker: calendar (earnings within seven days),
 * the blue round D (ex-dividend from 2 trading days before to 1 after), the
 * red square D (recent dilutive filing), N (live news, click for headlines),
 * and an alert bell (signed-in user has an active price alert). Each signal
 * comes from a shared, SWR-deduped hook, so rendering this for many tickers
 * (or many open popups) costs at most one fetch per signal.
 *
 * Renders nothing when no signal applies — safe to drop in next to a price
 * or symbol anywhere.
 */
export function TickerBadges({
  ticker,
  className,
}: {
  ticker: string;
  className?: string;
}): React.JSX.Element | null {
  const up = (ticker || '').toUpperCase();
  const diluting = useDilutingSymbols();
  const newsMap = useLiveNewsSymbols();
  const earningsSoon = useEarningsSoonSymbols();
  const dividendSoon = useDividendSoonSymbols();
  const alertSymbols = useAlertSymbols();

  const news = newsMap.get(up) ?? [];
  const earningsDate = earningsSoon.get(up);
  // The tier gates rendering as well as colour: a date the shared map still
  // carries but that has since slipped past must not draw a badge.
  const earningsTier = earningsDate ? earningsBadgeTier(earningsDate) : null;
  const hasER = earningsDate != null && earningsTier != null;
  // Same for the dividend: the map is refreshed every 30 min, the window
  // moves at midnight.
  const dividend = dividendSoon.get(up);
  const hasDiv = dividend != null && dividendBadgePhase(dividend.exDate) != null;
  const hasD = diluting.has(up);
  const hasN = news.length > 0;
  const hasAlert = alertSymbols.has(up);

  if (!hasER && !hasDiv && !hasD && !hasN && !hasAlert) return null;

  return (
    <span className={cn('inline-flex items-center gap-1 flex-shrink-0', className)}>
      {hasER && (
        <span
          title={formatEarningsBadgeTooltip(earningsDate!)}
          aria-label={formatEarningsBadgeTooltip(earningsDate!)}
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded border',
            EARNINGS_TIER_CLASSES[earningsTier!],
          )}
        >
          <CalendarClock size={9} aria-hidden="true" />
        </span>
      )}
      {hasDiv && <DividendGlyph info={dividend!} />}
      {/* N sits between the two D's — blue round (dividend) and red square
          (dilution) — so they don't read as one doubled mark. */}
      {hasN && <LiveNewsBadge items={news} />}
      {hasD && <DilutionBadge asSpan />}
      {hasAlert && (
        <span
          title="You have an active price alert on this ticker"
          aria-label="Active price alert"
          className="inline-flex items-center justify-center h-4 w-4 rounded bg-sky-500/15 border border-sky-500/30"
        >
          <Bell size={10} className="fill-sky-400 text-sky-400" />
        </span>
      )}
    </span>
  );
}

export default TickerBadges;
